import React, { useState, useRef, useEffect } from 'react';

export type SortMode = 'relevant' | 'date_desc' | 'date_asc';

interface SortModeToggleProps {
  value: SortMode;
  onChange: (mode: SortMode) => void;
  className?: string;
  /** Accessible label for the control */
  'aria-label'?: string;
}

const LABELS: Record<SortMode, string> = {
  relevant: 'Relevant',
  date_desc: 'Date ↓',
  date_asc: 'Date ↑',
};

/**
 * Dropdown to choose sort mode for match listings.
 * - relevant: closest to now first (current behaviour)
 * - date_desc: newest matches first
 * - date_asc: oldest matches first
 */
const SortModeToggle: React.FC<SortModeToggleProps> = ({
  value,
  onChange,
  className = '',
  'aria-label': ariaLabel = 'Sort matches',
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleSelect = (mode: SortMode) => {
    onChange(mode);
    setOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      aria-label={ariaLabel}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center px-2.5 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <span className="mr-1 text-gray-500">Sort:</span>
        <span className="font-medium text-gray-900">{LABELS[value]}</span>
        <svg
          className="w-3 h-3 ml-1 text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-40 rounded-md shadow-lg bg-white border border-gray-200 z-50">
          <div className="py-1 text-xs sm:text-sm">
            <button
              type="button"
              onClick={() => handleSelect('relevant')}
              className={`w-full text-left px-3 py-1.5 flex items-center justify-between hover:bg-gray-50 ${
                value === 'relevant' ? 'text-blue-600 font-medium' : 'text-gray-700'
              }`}
            >
              <span>Relevant</span>
              {value === 'relevant' && <span>●</span>}
            </button>
            <button
              type="button"
              onClick={() => handleSelect('date_desc')}
              className={`w-full text-left px-3 py-1.5 flex items-center justify-between hover:bg-gray-50 ${
                value === 'date_desc' ? 'text-blue-600 font-medium' : 'text-gray-700'
              }`}
            >
              <span>Date ↓ (newest first)</span>
              {value === 'date_desc' && <span>●</span>}
            </button>
            <button
              type="button"
              onClick={() => handleSelect('date_asc')}
              className={`w-full text-left px-3 py-1.5 flex items-center justify-between hover:bg-gray-50 ${
                value === 'date_asc' ? 'text-blue-600 font-medium' : 'text-gray-700'
              }`}
            >
              <span>Date ↑ (oldest first)</span>
              {value === 'date_asc' && <span>●</span>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SortModeToggle;


